import APIService from "./APIService";

/**
 * TrainingAPI - API service for the Training feature
 *
 * Handles all training-related API calls including:
 * - Training modules listing and details
 * - Training sessions (progress tracking)
 * - Quizzes and quiz submissions
 * - Badges and achievements
 * - Certificates
 * - Categories
 */
const TrainingAPI = {
  // ==================== TRAINING MODULES ====================

  /**
   * Get all active trainings with user progress
   * @returns {Promise<Object>} Response with trainings array
   */
  getAllTrainings: async () => {
    console.log("[TrainingAPI][getAllTrainings] Fetching all trainings");
    try {
      const response = await APIService.get("/api/v1/app/training/list");
      console.log("[TrainingAPI][getAllTrainings] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getAllTrainings] Error:", error);
      throw error;
    }
  },

  /**
   * Get a specific training by UUID
   * @param {string} trainingUuid - Training UUID
   * @returns {Promise<Object>} Response with training details
   */
  getTrainingByUuid: async (trainingUuid) => {
    console.log(
      `[TrainingAPI][getTrainingByUuid] Fetching training: ${trainingUuid}`,
    );
    try {
      const response = await APIService.get(
        `/api/v1/app/training/${trainingUuid}`,
      );
      console.log("[TrainingAPI][getTrainingByUuid] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getTrainingByUuid] Error:", error);
      throw error;
    }
  },

  /**
   * Get trainings by category
   * @param {string} category - Category name
   * @returns {Promise<Object>} Response with trainings array
   */
  getTrainingsByCategory: async (category) => {
    console.log(
      `[TrainingAPI][getTrainingsByCategory] Fetching category: ${category}`,
    );
    try {
      const response = await APIService.get(
        `/api/v1/app/training/category/${category}`,
      );
      console.log("[TrainingAPI][getTrainingsByCategory] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getTrainingsByCategory] Error:", error);
      throw error;
    }
  },

  /**
   * Get featured trainings
   * @returns {Promise<Object>} Response with featured trainings array
   */
  getFeaturedTrainings: async () => {
    console.log(
      "[TrainingAPI][getFeaturedTrainings] Fetching featured trainings",
    );
    try {
      const response = await APIService.get("/api/v1/app/training/featured");
      console.log("[TrainingAPI][getFeaturedTrainings] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getFeaturedTrainings] Error:", error);
      throw error;
    }
  },

  /**
   * Search trainings by keyword
   * @param {string} query - Search query
   * @returns {Promise<Object>} Response with matching trainings
   */
  searchTrainings: async (query) => {
    console.log(`[TrainingAPI][searchTrainings] Searching: ${query}`);
    try {
      const response = await APIService.get(
        `/api/v1/app/training/search?q=${encodeURIComponent(query)}`,
      );
      console.log("[TrainingAPI][searchTrainings] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][searchTrainings] Error:", error);
      throw error;
    }
  },

  /**
   * Get recommended trainings for the user
   * @param {number} limit - Maximum number of recommendations (default: 5)
   * @returns {Promise<Object>} Response with recommended trainings
   */
  getRecommendedTrainings: async (limit = 5) => {
    console.log(
      `[TrainingAPI][getRecommendedTrainings] Fetching recommendations, limit: ${limit}`,
    );
    try {
      const response = await APIService.get(
        `/api/v1/app/training/recommended?limit=${limit}`,
      );
      console.log("[TrainingAPI][getRecommendedTrainings] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getRecommendedTrainings] Error:", error);
      throw error;
    }
  },

  // ==================== TRAINING SESSIONS ====================

  /**
   * Start or resume a training session
   * @param {string} trainingUuid - Training UUID
   * @returns {Promise<Object>} Response with session details
   */
  startTraining: async (trainingUuid) => {
    console.log(
      `[TrainingAPI][startTraining] Starting training: ${trainingUuid}`,
    );
    try {
      const response = await APIService.post(
        "/api/v1/app/training/session/start",
        {
          trainingUuid: trainingUuid,
        },
      );
      console.log("[TrainingAPI][startTraining] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][startTraining] Error:", error);
      throw error;
    }
  },

  /**
   * Mark a training as complete (for trainings without quizzes)
   * @param {Object} data - Completion data
   * @param {number} data.sessionId - Session ID
   * @param {number} [data.timeSpentSeconds] - Optional time spent in seconds
   * @returns {Promise<Object>} Response with updated session
   */
  markComplete: async (data) => {
    console.log("[TrainingAPI][markComplete] Marking training complete:", data);
    try {
      const response = await APIService.post(
        "/api/v1/app/training/session/complete",
        data,
      );
      console.log("[TrainingAPI][markComplete] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][markComplete] Error:", error);
      throw error;
    }
  },

  /**
   * Get active training sessions
   * @returns {Promise<Object>} Response with active sessions array
   */
  getActiveSessions: async () => {
    console.log("[TrainingAPI][getActiveSessions] Fetching active sessions");
    try {
      const response = await APIService.get(
        "/api/v1/app/training/sessions/active",
      );
      console.log("[TrainingAPI][getActiveSessions] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getActiveSessions] Error:", error);
      throw error;
    }
  },

  /**
   * Get completed training sessions
   * @returns {Promise<Object>} Response with completed sessions array
   */
  getCompletedSessions: async () => {
    console.log(
      "[TrainingAPI][getCompletedSessions] Fetching completed sessions",
    );
    try {
      const response = await APIService.get(
        "/api/v1/app/training/sessions/completed",
      );
      console.log("[TrainingAPI][getCompletedSessions] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getCompletedSessions] Error:", error);
      throw error;
    }
  },

  /**
   * Get all training sessions for user
   * @returns {Promise<Object>} Response with all sessions array
   */
  getAllSessions: async () => {
    console.log("[TrainingAPI][getAllSessions] Fetching all sessions");
    try {
      const response = await APIService.get("/api/v1/app/training/sessions");
      console.log("[TrainingAPI][getAllSessions] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getAllSessions] Error:", error);
      throw error;
    }
  },

  // ==================== QUIZZES ====================

  /**
   * Get quiz for a training
   * @param {string} trainingUuid - Training UUID
   * @returns {Promise<Object>} Response with quiz metadata
   */
  getQuizForTraining: async (trainingUuid) => {
    console.log(
      `[TrainingAPI][getQuizForTraining] Fetching quiz for training: ${trainingUuid}`,
    );
    try {
      const response = await APIService.get(
        `/api/v1/app/training/${trainingUuid}/quiz`,
      );
      console.log("[TrainingAPI][getQuizForTraining] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getQuizForTraining] Error:", error);
      throw error;
    }
  },

  /**
   * Get quiz with questions for taking
   * @param {number} quizId - Quiz ID
   * @returns {Promise<Object>} Response with quiz and questions (no answers)
   */
  getQuizWithQuestions: async (quizId) => {
    console.log(`[TrainingAPI][getQuizWithQuestions] Fetching quiz: ${quizId}`);
    try {
      const response = await APIService.get(
        `/api/v1/app/training/quiz/${quizId}`,
      );
      console.log("[TrainingAPI][getQuizWithQuestions] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getQuizWithQuestions] Error:", error);
      throw error;
    }
  },

  /**
   * Submit quiz answers
   * @param {Object} submissionData - Quiz submission data
   * @param {number} submissionData.sessionId - Training session ID
   * @param {number} submissionData.quizId - Quiz ID
   * @param {Object} submissionData.answers - {questionUuid: selectedAnswer}
   * @param {number} [submissionData.timeSpentSeconds] - Time spent on quiz
   * @returns {Promise<Object>} Response with quiz result
   */
  submitQuiz: async (submissionData) => {
    console.log("[TrainingAPI][submitQuiz] Submitting quiz:", submissionData);
    try {
      const response = await APIService.post(
        "/api/v1/app/training/quiz/submit",
        submissionData,
      );
      console.log("[TrainingAPI][submitQuiz] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][submitQuiz] Error:", error);
      throw error;
    }
  },

  // ==================== PROGRESS & STATISTICS ====================

  /**
   * Get comprehensive training progress for user
   * @returns {Promise<Object>} Response with progress data
   */
  getUserProgress: async () => {
    console.log("[TrainingAPI][getUserProgress] Fetching user progress");
    try {
      const response = await APIService.get("/api/v1/app/training/progress");
      console.log("[TrainingAPI][getUserProgress] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getUserProgress] Error:", error);
      throw error;
    }
  },

  // ==================== BADGES ====================

  /**
   * Get all available badges with user earned status
   * @returns {Promise<Object>} Response with badges array
   */
  getAllBadges: async () => {
    console.log("[TrainingAPI][getAllBadges] Fetching all badges");
    try {
      const response = await APIService.get("/api/v1/app/training/badges");
      console.log("[TrainingAPI][getAllBadges] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getAllBadges] Error:", error);
      throw error;
    }
  },

  /**
   * Get user's earned badges
   * @returns {Promise<Object>} Response with earned badges array
   */
  getEarnedBadges: async () => {
    console.log("[TrainingAPI][getEarnedBadges] Fetching earned badges");
    try {
      const response = await APIService.get(
        "/api/v1/app/training/badges/earned",
      );
      console.log("[TrainingAPI][getEarnedBadges] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getEarnedBadges] Error:", error);
      throw error;
    }
  },

  // ==================== CERTIFICATES ====================

  /**
   * Get user's certificates
   * @returns {Promise<Object>} Response with certificates array
   */
  getUserCertificates: async () => {
    console.log("[TrainingAPI][getUserCertificates] Fetching certificates");
    try {
      const response = await APIService.get(
        "/api/v1/app/training/certificates",
      );
      console.log("[TrainingAPI][getUserCertificates] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getUserCertificates] Error:", error);
      throw error;
    }
  },

  /**
   * Verify a certificate by number
   * @param {string} certificateNumber - Certificate number (e.g., KS-20240115-12345)
   * @returns {Promise<Object>} Response with certificate validity
   */
  verifyCertificate: async (certificateNumber) => {
    console.log(
      `[TrainingAPI][verifyCertificate] Verifying: ${certificateNumber}`,
    );
    try {
      const response = await APIService.get(
        `/api/v1/app/training/certificates/verify/${certificateNumber}`,
      );
      console.log("[TrainingAPI][verifyCertificate] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][verifyCertificate] Error:", error);
      throw error;
    }
  },

  // ==================== CATEGORIES ====================

  /**
   * Get all training categories
   * @returns {Promise<Object>} Response with categories array
   */
  getAllCategories: async () => {
    console.log("[TrainingAPI][getAllCategories] Fetching categories");
    try {
      const response = await APIService.get("/api/v1/app/training/categories");
      console.log("[TrainingAPI][getAllCategories] Success:", response);
      return response;
    } catch (error) {
      console.error("[TrainingAPI][getAllCategories] Error:", error);
      throw error;
    }
  },
};

export default TrainingAPI;
